import { useEffect, useRef, useState } from 'react';
import { sendJson, errorMessage } from '@/lib/api';
import { isAllowedPortalTarget } from '@/lib/portal-target';

interface MintedStaffToken {
  readonly token: string;
  readonly expiresAt: number;
}

/**
 * The landing point of a client portal's staff door.
 *
 * A portal cannot host a staff Clerk session (different registrable domain),
 * so its staff sign-in redirects here with `?to=<portal URL>`. This page runs
 * behind the admin app's sign-in gate — a signed-out staff member sees the
 * normal sign-in first and lands back here with the URL intact. It validates
 * the target, mints a short-lived staff portal token, and bounces straight
 * back with the token in the URL fragment.
 *
 * @returns A brief in-flight notice, or the error that stopped the handoff.
 */
export function PortalAccessPage(): React.ReactElement {
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // The effect redirects away on success; the ref guards StrictMode's
    // double-invoke from minting twice.
    if (started.current) return;
    started.current = true;

    void (async (): Promise<void> => {
      const to = new URLSearchParams(window.location.search).get('to');
      if (!to) {
        setError('No portal address to return to.');
        return;
      }

      try {
        const response = await fetch('/api/v3/portal/public/config');
        const payload = (await response.json()) as {
          data?: { portalBaseDomain?: unknown };
        };
        const baseDomain =
          typeof payload.data?.portalBaseDomain === 'string' ? payload.data.portalBaseDomain : '';

        if (!isAllowedPortalTarget(to, baseDomain)) {
          setError('That address is not one of our client portals.');
          return;
        }

        const minted = await sendJson<MintedStaffToken>('post', '/staff/portal-token');
        window.location.replace(`${to}#staffToken=${encodeURIComponent(minted.token)}`);
      } catch (err) {
        setError(errorMessage(err));
      }
    })();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-portal-bg p-6">
      <div className="max-w-md text-center">
        {error ? (
          <>
            <h1 className="text-portal-h2 font-bold text-portal-ink">
              Could not open the portal
            </h1>
            <p className="mt-2 text-portal-small text-portal-muted">{error}</p>
          </>
        ) : (
          <p className="text-portal-small text-portal-muted" aria-busy="true">
            Opening the client portal…
          </p>
        )}
      </div>
    </div>
  );
}
